import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiPlus, FiTrash2, FiSave, FiAlertCircle } from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "../components/ToastProvider";
import "../styles/Dashboard.css";
import "../styles/Modal.css";

export default function AdvancedABTestingEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState(null);
  
  const [destinations, setDestinations] = useState([]);
  const [conversionData, setConversionData] = useState(null);
  
  const fetchLink = useCallback(async () => {
    try {
      const data = await api.getLink(id);
      setLink(data);
      
      let parsed = [];
      try {
        if (data.ab_destinations) {
          parsed = JSON.parse(data.ab_destinations);
        }
      } catch (e) {}
      
      // Initialize with original url if empty
      if (parsed.length === 0) {
        parsed = [{ id: Date.now().toString(), url: data.original_url, weight: 100 }];
      } else {
        // Ensure each has a unique id for React keys
        parsed = parsed.map(p => ({ ...p, id: p.id || Math.random().toString(36).substr(2, 9) }));
      }
      
      setDestinations(parsed);

      // Fetch conversion data if conversions are enabled
      if (data.conversions_enabled) {
        try {
          const convData = await api.getLinkConversions(id);
          setConversionData(convData);
        } catch (e) {
          // Conversions might not be available
        }
      }
    } catch (err) {
      addToast("Failed to load link", "error");
      navigate("/advanced/ab-testing");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, addToast]);

  useEffect(() => {
    document.title = "Configure A/B Test - LinkSquire";
    fetchLink();
  }, [fetchLink]);

  const totalWeight = destinations.reduce((sum, d) => sum + (Number(d.weight) || 0), 0);

  // Helper to get conversion data for a specific destination URL
  const getConversionsForDest = (url) => {
    if (!conversionData || !conversionData.by_destination) return null;
    return conversionData.by_destination.find(d => d.destination_url === url);
  };

  const handleAddDest = () => {
    setDestinations([...destinations, { id: Date.now().toString(), url: "", weight: 0 }]);
  };

  const handleRemoveDest = (destId) => {
    if (destinations.length <= 1) {
      addToast("Must have at least one destination", "error");
      return;
    }
    setDestinations(destinations.filter(d => d.id !== destId));
  };

  const handleUpdateDest = (destId, field, value) => {
    setDestinations(destinations.map(d => {
      if (d.id === destId) {
        return { ...d, [field]: value };
      }
      return d;
    }));
  };

  const handleDistributeEvenly = () => {
    if (destinations.length === 0) return;
    const baseWeight = Math.floor(100 / destinations.length);
    let remainder = 100 - (baseWeight * destinations.length);
    
    setDestinations(destinations.map((d, i) => ({
      ...d,
      weight: i < remainder ? baseWeight + 1 : baseWeight
    })));
  };

  const handleSave = async () => {
    if (totalWeight !== 100) {
      addToast("Total weight must equal 100%", "error");
      return;
    }
    
    for (const d of destinations) {
      if (!d.url) {
        addToast("All destinations must have a valid URL", "error");
        return;
      }
    }

    setSaving(true);
    try {
      const cleanDestinations = destinations.map(d => ({ url: d.url, weight: Number(d.weight) }));
      await api.updateLink(id, {
        ab_testing_enabled: true,
        ab_destinations: JSON.stringify(cleanDestinations)
      });
      addToast("A/B Testing configuration saved", "success");
      navigate("/advanced/ab-testing");
    } catch (err) {
      addToast(err.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm("Are you sure you want to disable A/B testing for this link? Traffic will route only to the original destination URL.")) return;
    
    setSaving(true);
    try {
      await api.updateLink(id, { ab_testing_enabled: false });
      addToast("A/B Testing disabled", "success");
      navigate("/advanced/ab-testing");
    } catch (err) {
      addToast(err.message || "Failed to disable", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn-sm" onClick={() => navigate("/advanced/ab-testing")} style={{ padding: '6px', background: 'transparent', border: '1px solid var(--border)' }}>
            <FiArrowLeft />
          </button>
          <h1 style={{ fontSize: '20px' }}>Configure A/B Test</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-sm btn-sm-danger" onClick={handleDisable} disabled={saving}>
            {saving ? <span className="spinner-sm dark" /> : null} Disable A/B Test
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || totalWeight !== 100}>
            {saving ? <><span className="spinner-sm" /> Saving...</> : <><FiSave /> Save Configuration</>}
          </button>
        </div>
      </div>
      
      <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', fontWeight: 600 }}>Editing Link</div>
        <div style={{ fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {link.title || link.short_code} 
          <a href={link.short_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 400, textDecoration: 'none' }}>{link.short_url}</a>
        </div>
      </div>

      <div className="links-section" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '16px', marginBottom: '4px' }}>Destinations & Weights</h2>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Traffic will be routed randomly according to the percentages you define.</p>
          </div>
          <button className="btn-sm" onClick={handleDistributeEvenly}>Distribute Evenly</button>
        </div>

        {totalWeight !== 100 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: '#fff3cd', color: '#856404', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', fontWeight: 500 }}>
            <FiAlertCircle /> Total weight is {totalWeight}%. It must be exactly 100%.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {destinations.map((dest, index) => {
            const convInfo = getConversionsForDest(dest.url);
            return (
              <div key={dest.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '16px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--surface)' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '6px' }}>Destination URL {index + 1}</label>
                  <input 
                    type="url" 
                    value={dest.url} 
                    onChange={(e) => handleUpdateDest(dest.id, 'url', e.target.value)} 
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px' }} 
                    placeholder="https://example.com/variant-a"
                  />
                  {convInfo && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      <span style={{ color: '#7c3aed', fontWeight: 500 }}>{convInfo.conversions}</span> conversions
                      {convInfo.value > 0 && <span> • ${convInfo.value.toFixed(2)} value</span>}
                    </div>
                  )}
                </div>
                <div style={{ width: '120px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '6px' }}>Weight (%)</label>
                  <input 
                    type="number" 
                    min="0" 
                    max="100" 
                    value={dest.weight} 
                    onChange={(e) => handleUpdateDest(dest.id, 'weight', parseInt(e.target.value) || 0)} 
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: '13px' }} 
                  />
                </div>
                <div style={{ paddingTop: '24px' }}>
                  <button 
                    className="btn-sm btn-sm-danger" 
                    style={{ padding: '8px', height: '35px' }} 
                    onClick={() => handleRemoveDest(dest.id)}
                    data-tooltip="Remove destination"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <button 
          className="btn-sm" 
          style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '6px', borderStyle: 'dashed', width: '100%', justifyContent: 'center', padding: '12px' }}
          onClick={handleAddDest}
        >
          <FiPlus /> Add another destination
        </button>
      </div>
    </div>
  );
}
